import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import AppLayout from '../components/AppLayout';
import { getKYCTierInfo } from '../lib/utils';
import { 
  User, Shield, CreditCard, FileText, HelpCircle, 
  Info, LogOut, ChevronRight, Moon, Sun, Smartphone,
  Bell, Lock, Wallet, Scissors, Heart, Crown, Briefcase
} from 'lucide-react';

const MorePage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const tierInfo = getKYCTierInfo(user?.kyc_tier || 1);

  const menuSections = [
    {
      title: 'Features',
      items: [
        { icon: Scissors, label: 'Clips', path: '/clips', badge: 'New', badgeColor: 'bg-primary/20 text-primary' },
        { icon: Heart, label: 'Tips & Gifts', path: '/tipping', badge: 'New', badgeColor: 'bg-primary/20 text-primary' },
        { icon: Crown, label: 'Subscriptions', path: '/subscriptions', badge: 'New', badgeColor: 'bg-primary/20 text-primary' },
        { icon: Briefcase, label: 'Marketplace', path: '/marketplace', badge: 'New', badgeColor: 'bg-primary/20 text-primary' },
      ]
    },
    {
      title: 'Account',
      items: [
        { icon: Shield, label: 'Verification Status', path: '/kyc', badge: `Tier ${user?.kyc_tier || 1}`, badgeColor: tierInfo.bgColor },
        { icon: Wallet, label: 'Account Limits', path: '/kyc' },
        { icon: CreditCard, label: 'Saved Accounts', path: '/withdraw' },
        { icon: FileText, label: 'Account Statement', path: '/transactions' },
      ]
    },
    {
      title: 'Security',
      items: [
        { icon: Lock, label: 'Change PIN', path: '/settings' },
        { icon: Smartphone, label: 'Biometric Login', path: '/settings' },
        { icon: Shield, label: 'Two-Factor Auth', path: '/settings' },
        { icon: Bell, label: 'Notifications', path: '/notifications' },
      ]
    },
    {
      title: 'Preferences',
      items: [
        { icon: theme === 'dark' ? Moon : Sun, label: 'Appearance', action: () => setTheme(theme === 'dark' ? 'light' : 'dark'), value: theme === 'dark' ? 'Dark' : 'Light' },
      ]
    },
    {
      title: 'Support',
      items: [
        { icon: HelpCircle, label: 'Help Center', path: '/help' },
        { icon: Info, label: 'About LekeeWallet', path: '/about' },
      ]
    },
  ];

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-24 lg:pb-0">
        {/* Profile Header */}
        <div className="p-6 lg:p-0 lg:mb-8 bg-gradient-to-b from-primary/10 to-transparent lg:bg-none lg:rounded-2xl lg:border lg:border-white/10 lg:bg-card lg:p-6">
          <div className="flex items-center gap-4">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt={user.full_name} className="w-16 h-16 rounded-full object-cover border-2 border-white/20" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-2xl font-bold">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{user?.full_name}</h1>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <span className={`inline-block mt-1 text-xs px-2 py-1 rounded-full ${tierInfo.bgColor} ${tierInfo.color}`}>{tierInfo.name} Verified</span>
            </div>
            <Link to="/settings" className="p-2 hover:bg-white/5 rounded-lg transition-colors"><User className="w-6 h-6" /></Link>
          </div>
        </div>
        
        {/* Menu Sections */}
        <div className="px-6 lg:px-0 space-y-6">
          <div className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-6 lg:space-y-0">
            {menuSections.map((section) => (
              <div key={section.title}>
                <h2 className="text-sm font-medium text-muted-foreground mb-3">{section.title}</h2>
                <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
                  {section.items.map((item, idx) => {
                    const Icon = item.icon;
                    const isLast = idx === section.items.length - 1;
                    if (item.action) {
                      return (
                        <button key={item.label} onClick={item.action} className={`w-full flex items-center gap-4 p-4 hover:bg-white/5 transition-colors ${!isLast ? 'border-b border-white/10' : ''}`} data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}>
                          <Icon className="w-5 h-5 text-muted-foreground" /><span className="flex-1 text-left">{item.label}</span>
                          {item.value && <span className="text-sm text-muted-foreground">{item.value}</span>}<ChevronRight className="w-5 h-5 text-muted-foreground" />
                        </button>
                      );
                    }
                    return (
                      <Link key={item.label} to={item.path} className={`flex items-center gap-4 p-4 hover:bg-white/5 transition-colors ${!isLast ? 'border-b border-white/10' : ''}`} data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}>
                        <Icon className="w-5 h-5 text-muted-foreground" /><span className="flex-1">{item.label}</span>
                        {item.badge && <span className={`text-xs px-2 py-1 rounded-full ${item.badgeColor}`}>{item.badge}</span>}
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          <button onClick={handleLogout} className="w-full flex items-center gap-4 p-4 bg-card border border-white/10 rounded-xl hover:bg-white/5 transition-colors text-red-500 lg:max-w-sm" data-testid="logout-btn">
            <LogOut className="w-5 h-5" /><span>Log Out</span>
          </button>
          <p className="text-center text-xs text-muted-foreground py-4">LekeeWallet v1.0.0</p>
        </div>
      </div>
    </AppLayout>
  );
};

export default MorePage;
